import React, { useState, useEffect, useMemo, useCallback, useRef } from 'react';
import styled, { keyframes } from 'styled-components';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { toast } from 'react-toastify';
import { MapContainer, TileLayer, Marker, Popup } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useTheme } from '../context/ThemeContext';
import { useTimezone } from '../context/TimezoneContext';
import { useRole } from '../hooks/useRole';
import { useReadOnly } from '../hooks/useReadOnly';
import useOrganizationConfig from '../hooks/useOrganizationConfig';
import { ThemedText, Button, MiniMap, MapLinksDropdown, PageHeader, ShareReportModal, Avatar } from '../components';
import LocationEditorMap from '../components/LocationEditorMap';
import { Layout } from '../components/Layout';
import { StandaloneVizSubmissionCard } from '../components/standalone/StandaloneVizSubmissionCard';
import { ReadOnlyFormRenderer } from '../components/FormBuilder/ReadOnlyFormRenderer';
import { LinkExistingSubmissionPicker } from '../components/LinkExistingSubmissionPicker';
import { formSchemaService, formSubmissionService, getSubmissionStatusLabel, SubmissionStatus, workOrderService } from '../api';
import pdfTemplateService from '../api/services/pdfTemplateService';
import pdfReportService from '../api/services/pdfReportService';
import { convertFormSchemaDefinitionToFields } from '../utils/schemaConverter';
import { evaluateRules } from '../utils/ruleEvaluator';
import { formatSubmissionDate, formatDateOnly } from '../utils/dateUtils';
import { renderDetailValue } from '../registry/renderers';
import { isMediaType } from '../registry/resolvers';
import { StatusPalette, SubmissionStatusColors, WorkOrderStatusColors, LabReportStatusColors, withOpacity } from '../constants/statusColors';
import auditLogService from '../api/services/auditLogService';
import labReportService, { getLabReportStatusLabel } from '../api/services/labReportService';
import { renderFormIcon, renderFieldTypeIcon } from '../utils/iconUtils';
import { List, Table2, Grid3x3, ChevronRight, ChevronDown, Pencil, Check, Cloud, X, Clock, Wifi, ArrowLeft as ArrowLeftIcon, MessageCircle, Trash2, Send, Link2 } from 'lucide-react';

// Fix for default marker icons in react-leaflet
import markerIcon2x from 'leaflet/dist/images/marker-icon-2x.png';
import markerIcon from 'leaflet/dist/images/marker-icon.png';
import markerShadow from 'leaflet/dist/images/marker-shadow.png';

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: markerIcon2x,
  iconUrl: markerIcon,
  shadowUrl: markerShadow,
});

const PageContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
`;

// Header components removed in favor of reusable PageHeader

const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 350px;
  gap: ${props => props.theme.spacing.xl};

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
  }
`;

const MainContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Sidebar = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.lg};
`;

const Card = styled.div`
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border: 1px solid ${props => props.theme.colors.border};
  padding: ${props => props.theme.spacing.xl};
`;

const CardHeader = styled.div`
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.muted};
  cursor: pointer;
  user-select: none;

  input[type="checkbox"] {
    cursor: pointer;
    width: 16px;
    height: 16px;
  }

  &:hover {
    color: ${props => props.theme.colors.text};
  }
`;

const FieldRow = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.xs};
  padding: ${props => props.theme.spacing.md} 0;
  border-bottom: 1px solid ${props => props.theme.colors.border};

  &:last-child {
    border-bottom: none;
  }
`;

const FieldLabel = styled(ThemedText)`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
`;

const ReadOnlyLabel = styled(ThemedText)`
  color: ${props => props.theme.colors.muted};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.xs};
`;

const FieldValue = styled(ThemedText)`
  font-size: ${props => props.theme.typography.sizes.default};
`;

const StatusBadge = styled.span`
  padding: 6px ${props => props.theme.spacing.md};
  border-radius: ${props => props.theme.borderRadius.sm};
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 600;
  text-align: center;
  display: inline-block;
  ${props => {
    const c = SubmissionStatusColors[props.status];
    if (c) return `background-color: ${withOpacity(c)}; color: ${c};`;
    const themeC = [0, 5].includes(props.status) ? props.theme.colors.muted
      : props.status === 1 ? props.theme.colors.tint : props.theme.colors.muted;
    return `background-color: ${themeC}20; color: ${themeC};`;
  }}
`;

const spin = keyframes`
  from { transform: rotate(0deg); }
  to { transform: rotate(360deg); }
`;

const SpinIcon = styled.svg`
  animation: ${spin} 0.8s linear infinite;
  flex-shrink: 0;
`;

const LoadingContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  min-height: 300px;
  color: ${props => props.theme.colors.muted};
`;

const InfoRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  padding: ${props => props.theme.spacing.sm} 0;
  gap: ${props => props.theme.spacing.md};
`;

const Modal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.md};
  animation: fadeIn 0.2s ease;

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const ModalContent = styled.div`
  background-color: ${props => props.theme.colors.background};
  border-radius: 16px;
  padding: 28px;
  max-width: 900px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0 25px 50px -12px rgba(0, 0, 0, 0.25);
  animation: slideUp 0.3s ease;

  @keyframes slideUp {
    from {
      transform: translateY(20px);
      opacity: 0;
    }
    to {
      transform: translateY(0);
      opacity: 1;
    }
  }
`;

const ModalHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: ${props => props.theme.spacing.lg};
  padding-bottom: ${props => props.theme.spacing.md};
  border-bottom: 2px solid #f3f4f6;
`;

const LargeMap = styled.div`
  width: 100%;
  height: 500px;
  border-radius: 10px;
  overflow: hidden;
  margin-top: ${props => props.theme.spacing.md};
  position: relative;
  background-color: #f9fafb;
  border: 1.5px solid #e5e7eb;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.07);

  .leaflet-container {
    height: 100%;
    width: 100%;
  }
`;

const MapButtonsRow = styled.div`
  max-width: 300px;
  margin: ${props => props.theme.spacing.lg} auto 0;
`;

const ImageModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.9);
  display: ${props => props.isOpen ? 'flex' : 'none'};
  align-items: center;
  justify-content: center;
  z-index: 10000;
  padding: ${props => props.theme.spacing.xl};
`;

const ImageModalContent = styled.div`
  position: relative;
  max-width: 90vw;
  max-height: 90vh;
`;

const FullSizeImage = styled.img`
  max-width: 100%;
  max-height: 90vh;
  border-radius: ${props => props.theme.borderRadius.md};
`;

const CloseButton = styled.button`
  position: absolute;
  top: -40px;
  right: 0;
  background: none;
  border: none;
  color: white;
  font-size: 32px;
  cursor: pointer;
  padding: ${props => props.theme.spacing.sm};
  line-height: 1;

  &:hover {
    opacity: 0.7;
  }
`;

const ChildSubmissionsContainer = styled.div`
  margin-top: ${props => props.theme.spacing.lg};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  padding: ${props => props.theme.spacing.lg};
  background-color: ${props => props.theme.colors.backgroundSecondary}30;
`;

const ChildSubmissionCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-bottom: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  overflow: hidden;

  &:last-child {
    margin-bottom: 0;
  }
`;

const ChildSubmissionHeader = styled.div`
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  cursor: pointer;
  user-select: none;
  display: flex;
  justify-content: space-between;
  align-items: center;

  &:hover {
    background-color: ${props => props.theme.colors.backgroundSecondary}80;
  }
`;

const ChildSubmissionBody = styled.div`
  padding: ${props => props.theme.spacing.md};
  display: ${props => props.isCollapsed ? 'none' : 'block'};
`;

const ViewSelector = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.xs};
  background-color: ${props => props.theme.colors.background};
  width: fit-content;
`;

const ViewButton = styled.button`
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: none;
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: ${props => props.isActive ? props.theme.colors.tint : 'transparent'};
  color: ${props => props.isActive ? 'white' : props.theme.colors.text};
  cursor: pointer;
  font-size: ${props => props.theme.typography.sizes.small};
  font-weight: 500;
  transition: all 0.2s;

  &:hover {
    background-color: ${props => props.isActive ? props.theme.colors.tint : props.theme.colors.backgroundSecondary};
  }
`;

// Comment styled components
const CommentsList = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${props => props.theme.spacing.md};
`;

const CommentItem = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  align-items: flex-start;
`;

const CommentBubble = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.backgroundSecondary};
  border-radius: ${props => props.theme.borderRadius.lg};
  border-top-left-radius: ${props => props.theme.borderRadius.sm};
`;

const SystemCommentItem = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.xs} 0;
  color: ${props => props.theme.colors.muted};
  font-style: italic;
  font-size: ${props => props.theme.typography.sizes.small};
`;

const CommentHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 2px;
`;

const CommentAuthor = styled.span`
  font-weight: 600;
  font-size: ${props => props.theme.typography.sizes.small};
  color: ${props => props.theme.colors.text};
`;

const CommentTime = styled.span`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
`;

const CommentText = styled.p`
  margin: 0;
  font-size: ${props => props.theme.typography.sizes.body};
  color: ${props => props.theme.colors.text};
  white-space: pre-wrap;
  word-break: break-word;
  line-height: 1.5;
`;

const CommentInputArea = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.sm};
  margin-top: ${props => props.theme.spacing.md};
  padding-top: ${props => props.theme.spacing.md};
  border-top: 1px solid ${props => props.theme.colors.border};
  align-items: flex-start;
`;

const CommentTextarea = styled.textarea`
  flex: 1;
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.lg};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-family: inherit;
  font-size: ${props => props.theme.typography.sizes.body};
  resize: none;
  min-height: 44px;
  max-height: 120px;
  line-height: 1.5;
  transition: border-color 0.2s;

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
    box-shadow: 0 0 0 2px ${props => props.theme.colors.tint}20;
  }

  &::placeholder {
    color: ${props => props.theme.colors.muted};
  }
`;

const SendButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background-color: ${props => props.theme.colors.tint};
  color: white;
  cursor: pointer;
  flex-shrink: 0;
  transition: all 0.2s;
  margin-top: 4px;

  &:hover {
    transform: translateY(-1px);
    box-shadow: 0 2px 8px ${props => props.theme.colors.tint}40;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
    transform: none;
    box-shadow: none;
  }
`;

const DeleteCommentButton = styled.button`
  background: none;
  border: none;
  padding: 2px;
  cursor: pointer;
  color: ${props => props.theme.colors.muted};
  display: flex;
  align-items: center;
  opacity: 0;
  transition: all 0.2s;

  ${CommentBubble}:hover & {
    opacity: 1;
  }

  &:hover {
    color: ${props => props.theme.colors.danger || '#dc3545'};
  }
`;

const CommentEmptyState = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  padding: ${props => props.theme.spacing.xl} 0;
  color: ${props => props.theme.colors.muted};
`;

const TableContainer = styled.div`
  overflow-x: auto;
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const StyledTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: ${props => props.theme.colors.background};

  th, td {
    padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.md};
    text-align: left;
    border-bottom: 1px solid ${props => props.theme.colors.border};
  }

  th {
    background-color: ${props => props.theme.colors.backgroundSecondary};
    font-weight: 600;
    font-size: ${props => props.theme.typography.sizes.small};
    color: ${props => props.theme.colors.muted};
    position: sticky;
    top: 0;
    z-index: 1;
  }

  tr:hover td {
    background-color: ${props => props.theme.colors.backgroundSecondary}40;
  }

  tr:last-child td {
    border-bottom: none;
  }
`;

const GridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.md};
  margin-top: ${props => props.theme.spacing.md};
`;

const GridCard = styled.div`
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  padding: ${props => props.theme.spacing.md};
  background-color: ${props => props.theme.colors.background};
  transition: all 0.2s;

  &:hover {
    box-shadow: ${props => props.theme.shadows.md};
    transform: translateY(-2px);
  }
`;

const GridCardHeader = styled.div`
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.sm};
  padding-bottom: ${props => props.theme.spacing.xs};
  border-bottom: 1px solid ${props => props.theme.colors.border};
  color: ${props => props.theme.colors.tint};
`;

// Timeline styles
const TimelineContainer = styled.div`
  position: relative;
  padding-left: 24px;
`;

const TimelineItem = styled.div`
  position: relative;
  padding-bottom: ${props => props.isLast ? '0' : props.theme.spacing.lg};

  &::before {
    content: '';
    position: absolute;
    left: -24px;
    top: 6px;
    width: 12px;
    height: 12px;
    border-radius: 50%;
    background-color: ${props => props.isActive ? props.theme.colors.tint : props.theme.colors.muted};
    border: 2px solid ${props => props.isActive ? props.theme.colors.tint : props.theme.colors.border};
    z-index: 1;
  }

  &::after {
    content: '';
    position: absolute;
    left: -19px;
    top: 18px;
    width: 2px;
    height: calc(100% - 12px);
    background-color: ${props => props.isLast ? 'transparent' : props.theme.colors.border};
  }
`;

const TimelineLabel = styled.div`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.xs};
  margin-bottom: 2px;
`;

const TimelineIcon = styled.span`
  font-size: 14px;
`;

const TimelineDuration = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  background-color: ${props => props.theme.colors.tint}15;
  color: ${props => props.theme.colors.tint};
  padding: 2px 8px;
  border-radius: 12px;
  font-size: 11px;
  font-weight: 600;
  margin-top: 4px;
`;

const TimelineNote = styled.div`
  font-size: 11px;
  color: ${props => props.theme.colors.muted};
  margin-top: 2px;
  font-style: italic;
`;

// PDF Generation styled components
const TemplateSelect = styled.select`
  width: 100%;
  padding: ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.border};
  border-radius: ${props => props.theme.borderRadius.md};
  background: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  margin-bottom: ${props => props.theme.spacing.md};

  &:focus {
    outline: none;
    border-color: ${props => props.theme.colors.tint};
  }
`;

const GenerateButton = styled(Button)`
  width: 100%;
`;

// Styled components for rendering layout fields in submission view
const SubmissionHeader = styled.div`
  margin: ${props => props.theme.spacing.lg} 0 ${props => props.theme.spacing.md} 0;
  font-weight: 600;
  font-size: ${props => {
    const level = props.level || 'h3';
    const sizes = {
      h1: props.theme.typography.sizes.xxlarge,
      h2: props.theme.typography.sizes.xlarge,
      h3: props.theme.typography.sizes.large,
      h4: props.theme.typography.sizes.default,
      h5: props.theme.typography.sizes.default,
      h6: props.theme.typography.sizes.small,
    };
    return sizes[level] || props.theme.typography.sizes.default;
  }};
  color: ${props => props.theme.colors.text};
  text-align: ${props => props.align || 'left'};
  border-bottom: ${props => props.level === 'h1' || props.level === 'h2'
    ? `2px solid ${props.theme.colors.border}`
    : 'none'};
  padding-bottom: ${props => props.level === 'h1' || props.level === 'h2'
    ? props.theme.spacing.sm
    : '0'};
`;

const SubmissionDivider = styled.hr`
  border: none;
  height: 1px;
  background-color: ${props => props.theme.colors.border};
  margin: ${props => props.theme.spacing.lg} 0;
`;

const EditActionsBar = styled.div`
  display: flex;
  gap: ${props => props.theme.spacing.md};
  align-items: center;
  padding: ${props => props.theme.spacing.md} 0;
  border-top: 1px solid ${props => props.theme.colors.border};
  margin-top: ${props => props.theme.spacing.md};
`;

const ChildEditInput = styled.input`
  width: 100%;
  padding: ${props => props.theme.spacing.xs} ${props => props.theme.spacing.sm};
  border: 1px solid ${props => props.theme.colors.tint};
  border-radius: ${props => props.theme.borderRadius.sm};
  background-color: ${props => props.theme.colors.background};
  color: ${props => props.theme.colors.text};
  font-size: ${props => props.theme.typography.sizes.default};
  font-family: ${props => props.theme.typography.fontFamily};
  box-sizing: border-box;

  &:focus {
    outline: 2px solid ${props => props.theme.colors.tint};
    outline-offset: -1px;
  }
`;

// Helper function to format duration
const formatDuration = (startDate, endDate) => {
  if (!startDate || !endDate) return null;

  const start = new Date(startDate);
  const end = new Date(endDate);
  const diffMs = end - start;

  if (diffMs < 0) return null;

  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffDays > 0) {
    const hours = diffHours % 24;
    return `${diffDays}d ${hours}h`;
  }
  if (diffHours > 0) {
    const mins = diffMinutes % 60;
    return `${diffHours}h ${mins}m`;
  }
  if (diffMinutes > 0) {
    const secs = diffSeconds % 60;
    return `${diffMinutes}m ${secs}s`;
  }
  return `${diffSeconds}s`;
};

/**
 * Check if a field is a layout field (non-data field)
 * @param {Object} field - Field object with type property
 * @returns {boolean} True if field is layout-only
 */
const isLayoutField = (field) => {
  const layoutTypes = ['header', 'divider', 'spacer'];
  return layoutTypes.includes(field.type);
};

/**
 * Check if a field is a data field (collects user input)
 * @param {Object} field - Field object with type property
 * @returns {boolean} True if field collects data
 */
const isDataField = (field) => {
  return !isLayoutField(field);
};

/**
 * Check if a field has an empty value
 * @param {any} value - Field value to check
 * @returns {boolean} True if field value is empty
 */
const isFieldEmpty = (value) => {
  if (value === null || value === undefined || value === '') return true;
  if (Array.isArray(value) && value.length === 0) return true;
  if (typeof value === 'object' && Object.keys(value).length === 0) return true;
  return false;
};

/**
 * Render layout field in submission view
 * @param {Object} field - Field configuration
 * @param {Object} theme - Theme object
 * @returns {JSX.Element|null} Rendered layout element
 */
const renderLayoutField = (field, theme) => {
  switch (field.type) {
    case 'header':
      const headerText = field.props.text || field.props.label || '';
      if (!headerText) return null; // Skip empty headers

      return (
        <SubmissionHeader
          key={field.id}
          theme={theme}
          level={field.props.level || 'h3'}
          align={field.props.align || 'left'}
        >
          {headerText}
        </SubmissionHeader>
      );

    case 'divider':
      return <SubmissionDivider key={field.id} theme={theme} />;

    case 'spacer':
      // Spacers are purely for form building, skip in submission view
      return null;

    default:
      return null;
  }
};

export const SubmissionDetailPage = () => {
  const theme = useTheme();
  const { effectiveTimezone } = useTimezone();
  const navigate = useNavigate();
  const { formId, submissionId } = useParams();
  const { isSuperAdmin, isAdmin, user } = useRole();
  const readOnly = useReadOnly();
  const {
    allowEditSubmissionLocation,
    allowEditSubmissionDateTime,
    allowEditSubmissionSubmitter,
  } = useOrganizationConfig();

  // Format a Date as a value for <input type="datetime-local"> ("YYYY-MM-DDTHH:MM", local time).
  const toLocalInputValue = (d) => {
    const dt = d instanceof Date ? d : new Date(d);
    if (Number.isNaN(dt.getTime())) return '';
    const pad = (n) => String(n).padStart(2, '0');
    return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}T${pad(dt.getHours())}:${pad(dt.getMinutes())}`;
  };
  const metaInputStyle = {
    padding: '6px 8px',
    border: `1px solid ${theme.colors.border}`,
    borderRadius: theme.borderRadius.md,
    background: theme.colors.background,
    color: theme.colors.text,
    fontSize: '14px',
    maxWidth: '220px',
  };

  const [loading, setLoading] = useState(true);
  const [submission, setSubmission] = useState(null);
  const [formSchema, setFormSchema] = useState(null);
  const [formFields, setFormFields] = useState([]);
  const [submissionData, setSubmissionData] = useState({});
  const [locationModalOpen, setLocationModalOpen] = useState(false);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [collapsedChildren, setCollapsedChildren] = useState({});
  const [childViewModes, setChildViewModes] = useState({}); // Store view mode per field
  // Active link-existing picker state: which repeated-form field triggered it.
  const [linkPicker, setLinkPicker] = useState({ open: false, field: null });
  const [childFieldSchemas, setChildFieldSchemas] = useState({}); // Map: field.id → { fieldKey: { type } }
  const childSchemaCacheRef = useRef({}); // Cache: schemaId → fieldMap (avoids re-fetching)
  const [hideEmptyFields, setHideEmptyFields] = useState(false); // Toggle for hiding unfilled fields
  const [linkedWorkOrder, setLinkedWorkOrder] = useState(null);

  // Edit mode state
  const [isEditMode, setIsEditMode] = useState(false);
  const [editedFields, setEditedFields] = useState({});
  // Editable submission metadata (location / datetime / submitter), gated by org flags.
  // Keys present here override the submission on save: { latitude, longitude, locationName, submittedAtLocal, submitterName, submitterEmail }
  const [editedMeta, setEditedMeta] = useState({});
  const [editedChildFields, setEditedChildFields] = useState({}); // { [childId]: { [fieldKey]: value } }
  const [isSaving, setIsSaving] = useState(false);

  // Work order assignment state
  const [woModalOpen, setWoModalOpen] = useState(false);
  const [availableWorkOrders, setAvailableWorkOrders] = useState([]);
  const [loadingWorkOrders, setLoadingWorkOrders] = useState(false);
  const [assigningWorkOrder, setAssigningWorkOrder] = useState(false);
  const [woSearchTerm, setWoSearchTerm] = useState('');

  // Status change state
  const [statusUpdating, setStatusUpdating] = useState(false);
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [statusModalAction, setStatusModalAction] = useState(null); // 'approve' or 'reject'
  const [statusComment, setStatusComment] = useState('');

  // PDF generation state
  const [pdfTemplates, setPdfTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState(null);
  const [loadingTemplates, setLoadingTemplates] = useState(true);

  // Generated PDF reports state
  const [generatedReports, setGeneratedReports] = useState([]);
  const [loadingReports, setLoadingReports] = useState(false);
  const [generatingServerPdf, setGeneratingServerPdf] = useState(false);
  const [shareReportId, setShareReportId] = useState(null);

  // History / Audit state
  const [activeView, setActiveView] = useState('form');
  const [auditHistory, setAuditHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Lab Reports state
  const [submissionLabReports, setSubmissionLabReports] = useState([]);
  const [labReportsLoading, setLabReportsLoading] = useState(false);
  const [linkedLabSamples, setLinkedLabSamples] = useState([]);
  const [labSamplesLoading, setLabSamplesLoading] = useState(false);
  const [autoMatchLoading, setAutoMatchLoading] = useState(false);
  const [selectedAuditLog, setSelectedAuditLog] = useState(null);
  const [auditDetail, setAuditDetail] = useState(null);

  // Comments state
  const [comments, setComments] = useState([]);
  const [commentsLoading, setCommentsLoading] = useState(false);
  const [newComment, setNewComment] = useState('');
  const [submittingComment, setSubmittingComment] = useState(false);
  const [unreadCommentCount, setUnreadCommentCount] = useState(0);

  const loadAuditHistory = useCallback(async () => {
    if (!submissionId) return;
    try {
      setHistoryLoading(true);
      const response = await auditLogService.getEntityHistory('FormSubmission', submissionId);
      const items = Array.isArray(response) ? response : (response?.data || response?.$values || []);
      setAuditHistory(items);
    } catch (error) {
      console.error('Failed to load audit history:', error);
    } finally {
      setHistoryLoading(false);
    }
  }, [submissionId]);

  const loadAuditDetail = async (auditLogId) => {
    try {
      const response = await auditLogService.getLogById(auditLogId);
      const detail = response?.data || response;
      setAuditDetail(detail);
      setSelectedAuditLog(auditLogId);
    } catch (error) {
      console.error('Failed to load audit detail:', error);
    }
  };

  const loadLabReports = useCallback(async () => {
    if (!submissionId) return;
    setLabReportsLoading(true);
    setLabSamplesLoading(true);
    try {
      const [reportsRes, samplesRes] = await Promise.all([
        labReportService.getLabReports({ formSubmissionId: submissionId }),
        labReportService.getLabSamplesForSubmission(submissionId),
      ]);
      const data = reportsRes?.data || reportsRes;
      setSubmissionLabReports(data.items || data || []);
      const samples = samplesRes?.data || samplesRes;
      setLinkedLabSamples(Array.isArray(samples) ? samples : samples?.$values || []);
    } catch {
      console.error('Failed to load lab reports for submission');
    } finally {
      setLabReportsLoading(false);
      setLabSamplesLoading(false);
    }
  }, [submissionId]);

  const loadComments = useCallback(async () => {
    if (!submissionId) return;
    try {
      setCommentsLoading(true);
      const response = await formSubmissionService.getComments(submissionId);
      const items = Array.isArray(response) ? response : (response?.data || response?.$values || []);
      setComments(items);
      // Backend auto-marks as read on GET, so clear the local badge
      setUnreadCommentCount(0);
    } catch (error) {
      console.error('Failed to load comments:', error);
    } finally {
      setCommentsLoading(false);
    }
  }, [submissionId]);

  const handleAddComment = async () => {
    if (!newComment.trim() || submittingComment) return;
    try {
      setSubmittingComment(true);
      await formSubmissionService.addComment(submissionId, newComment.trim());
      setNewComment('');
      await loadComments();
    } catch (error) {
      console.error('Failed to add comment:', error);
      toast.error('Failed to add comment');
    } finally {
      setSubmittingComment(false);
    }
  };

  const handleDeleteComment = async (commentId) => {
    try {
      await formSubmissionService.deleteComment(commentId);
      await loadComments();
    } catch (error) {
      console.error('Failed to delete comment:', error);
      toast.error('Failed to delete comment');
    }
  };

  const handleAutoMatchSubmission = async () => {
    if (!submissionId) return;
    setAutoMatchLoading(true);
    try {
      await labReportService.attemptMatchForSubmission(submissionId);
      await loadLabReports();
    } catch (err) {
      console.error('Auto-match failed:', err);
    } finally {
      setAutoMatchLoading(false);
    }
  };

  const handleUnlinkSample = async (reportId, sampleId) => {
    try {
      await labReportService.unlinkSampleSubmission(reportId, sampleId);
      await loadLabReports();
    } catch (err) {
      console.error('Failed to unlink sample:', err);
    }
  };

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // Fetch submission details with child submissions
        const submissionResponse = await formSubmissionService.getSubmissionById(submissionId, { includeChildren: true });
        setSubmission(submissionResponse);

        // Extract field values from typed submission data (API v3.1+).
        const fieldValues = formSubmissionService.getSubmissionFields(submissionResponse.submissionData) || {};

        // Also merge in FormSubmissionField rows — auto-populated lab-result values live there,
        // not in SubmissionData JSON. Prefer stored values over SubmissionData on key collision.
        try {
          const storedFieldRows = await formSubmissionService.fetchSubmissionFields(submissionId);
          if (Array.isArray(storedFieldRows)) {
            for (const row of storedFieldRows) {
              if (row?.fieldKey && !(row.fieldKey in fieldValues)) {
                fieldValues[row.fieldKey] = row.value;
              }
            }
          }
        } catch (err) {
          // Non-fatal: detail view still works without stored-field merge.
          console.warn('Could not fetch auto-populated field rows', err);
        }

        setSubmissionData(fieldValues);

        // Fetch form schema
        const formResponse = await formSchemaService.getFormSchemaById(formId);
        setFormSchema(formResponse);

        // Convert schema definition to fields
        let fields = [];
        if (formResponse.schemaDefinition) {
          fields = convertFormSchemaDefinitionToFields(formResponse.schemaDefinition);
        } else if (formResponse.schemaJson) {
          // Backward compatibility
          try {
            const parsedSchema = JSON.parse(formResponse.schemaJson);
            fields = convertFormSchemaDefinitionToFields(parsedSchema);
          } catch (error) {
            console.error('Failed to parse legacy schemaJson:', error);
          }
        }
        setFormFields(fields);

        // Fetch linked work order if present
        const woId = submissionResponse.workOrderId || submissionResponse.workOrder?.id;
        if (woId) {
          // If the API returned the workOrder navigation property, use it directly
          if (submissionResponse.workOrder && submissionResponse.workOrder.title) {
            setLinkedWorkOrder(submissionResponse.workOrder);
          } else {
            try {
              const wo = await workOrderService.getWorkOrderById(woId);
              setLinkedWorkOrder(wo);
            } catch (woError) {
              console.error('Failed to fetch linked work order:', woError);
            }
          }
        }
      } catch (error) {
        console.error('Failed to fetch submission:', error);
        toast.error('Failed to load submission details');
      } finally {
        setLoading(false);
      }
    };

    if (formId && submissionId) {
      fetchData();
    }
  }, [formId, submissionId]);

  // Load unread comment count for this submission
  useEffect(() => {
    if (!submissionId) return;
    formSubmissionService.getUnreadCommentCounts()
      .then(counts => {
        if (counts && typeof counts === 'object') {
          setUnreadCommentCount(counts[Number(submissionId)] || 0);
        }
      })
      .catch(() => {});
  }, [submissionId]);

  // Load PDF templates when form schema is available
  useEffect(() => {
    const loadTemplates = async () => {
      if (!formSchema?.id) return;
      try {
        setLoadingTemplates(true);
        const templates = await pdfTemplateService.getTemplatesByFormId(formSchema.id);
        setPdfTemplates(templates || []);
        // Auto-select default or first template
        const defaultTemplate = templates.find(t => t.isDefault) || templates[0];
        if (defaultTemplate) setSelectedTemplateId(defaultTemplate.id);
      } catch (error) {
        console.error('Failed to load PDF templates:', error);
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();
  }, [formSchema?.id]);

  // Load child field schemas for repeated form icon display
  useEffect(() => {
    const loadChildSchemas = async () => {
      const repeatedFields = formFields.filter(
        f => f.type === 'repeated_form' && f.props?.configuration?.repeatedFormSchemaId
      );
      if (repeatedFields.length === 0) return;
      const updates = {};
      await Promise.all(repeatedFields.map(async (field) => {
        const schemaId = field.props.configuration.repeatedFormSchemaId;
        // Use cached schema if available
        if (childSchemaCacheRef.current[schemaId]) {
          updates[field.id] = childSchemaCacheRef.current[schemaId];
          return;
        }
        try {
          const childSchema = await formSchemaService.getFormSchemaById(schemaId);
          const childFields = convertFormSchemaDefinitionToFields(childSchema.schemaDefinition);
          const fieldMap = {};
          childFields.forEach(cf => { fieldMap[cf.id] = { id: cf.id, type: cf.type, label: cf.props?.label || cf.id, props: cf.props }; });
          childSchemaCacheRef.current[schemaId] = fieldMap;
          updates[field.id] = fieldMap;
        } catch {
          updates[field.id] = {};
        }
      }));
      setChildFieldSchemas(prev => ({ ...prev, ...updates }));
    };
    loadChildSchemas();
  }, [formFields]);

  // Advanced Field Rules — evaluate `format` styles once per submission.
  // ReadOnlyFormRenderer colours the main form fields itself; this map covers
  // the inline renderFieldValue path (e.g. repeated-form fields).
  const ruleFieldStyles = useMemo(() => {
    try {
      const canonical = formFields.map(f => ({
        key: f.id,
        options: f.props?.options,
        rules: f.props?.rules,
      }));
      return evaluateRules(canonical, submissionData || {}).fieldStyles || {};
    } catch (e) {
      return {};
    }
  }, [formFields, submissionData]);

  // Fields hidden by a visibility rule given the submitted answers — omit them
  // from the detail view (matches ReadOnlyFormRenderer; covers repeated_form too).
  const ruleHiddenKeySet = useMemo(() => {
    try {
      const canonical = formFields.map(f => ({
        key: f.id,
        options: f.props?.options,
        rules: f.props?.rules,
      }));
      return new Set(evaluateRules(canonical, submissionData || {}).hiddenFieldKeys || []);
    } catch (e) {
      return new Set();
    }
  }, [formFields, submissionData]);

  // Load generated PDF reports
  const loadGeneratedReports = useCallback(async () => {
    if (!submissionId) return;
    try {
      setLoadingReports(true);
      const reports = await pdfReportService.getReportsForSubmission(submissionId);
      setGeneratedReports(reports || []);
    } catch (error) {
      console.error('Failed to load generated reports:', error);
    } finally {
      setLoadingReports(false);
    }
  }, [submissionId]);

  useEffect(() => {
    loadGeneratedReports();
  }, [loadGeneratedReports]);

  const handleServerGeneratePdf = async () => {
    if (!selectedTemplateId) return;
    try {
      setGeneratingServerPdf(true);
      await pdfReportService.generateReport(Number(submissionId), selectedTemplateId);
      toast.success('PDF generation started');
      await loadGeneratedReports();
    } catch (error) {
      console.error('Failed to trigger PDF generation:', error);
      toast.error('Failed to generate PDF');
    } finally {
      setGeneratingServerPdf(false);
    }
  };

  const handleRegenerateReport = async (reportId) => {
    try {
      await pdfReportService.regenerateReport(reportId);
      toast.success('Report regeneration started');
      await loadGeneratedReports();
    } catch (error) {
      console.error('Failed to regenerate report:', error);
      toast.error('Failed to regenerate report');
    }
  };

  const handleDownloadReport = async (reportId) => {
    try {
      const result = await pdfReportService.getDownloadUrl(reportId);
      if (result?.downloadUrl) {
        window.open(result.downloadUrl, '_blank');
      }
    } catch (error) {
      console.error('Failed to get download URL:', error);
      toast.error('Failed to download report');
    }
  };

  const formatDate = (dateString) => {
    return formatSubmissionDate(dateString, effectiveTimezone);
  };

  const openWorkOrderModal = async () => {
    setWoModalOpen(true);
    setWoSearchTerm('');
    setLoadingWorkOrders(true);
    try {
      const result = await workOrderService.getWorkOrders();
      const woList = Array.isArray(result) ? result : (result?.items || result?.data || []);
      setAvailableWorkOrders(woList);
    } catch (err) {
      console.error('Failed to fetch work orders:', err);
      toast.error('Failed to load work orders');
    } finally {
      setLoadingWorkOrders(false);
    }
  };

  // Status change handlers
  const handleStatusChange = async (newStatus, comment = '') => {
    setStatusUpdating(true);
    try {
      if (newStatus === SubmissionStatus.APPROVED) {
        await formSubmissionService.approveSubmission(submissionId, comment);
      } else if (newStatus === SubmissionStatus.REJECTED) {
        await formSubmissionService.rejectSubmission(submissionId, comment);
      } else {
        await formSubmissionService.updateSubmissionStatus(submissionId, { status: newStatus, comments: comment });
      }
      // Refresh submission data
      const refreshed = await formSubmissionService.getSubmissionById(submissionId, { includeChildren: true });
      setSubmission(refreshed);
      toast.success(`Status updated to ${getSubmissionStatusLabel(newStatus)}`);
      setStatusModalOpen(false);
      setStatusComment('');
      setStatusModalAction(null);
    } catch (err) {
      console.error('Failed to update submission status:', err);
      toast.error(err.message || 'Failed to update status');
    } finally {
      setStatusUpdating(false);
    }
  };

  const openStatusModal = (action) => {
    setStatusModalAction(action);
    setStatusComment('');
    setStatusModalOpen(true);
  };

  const handleAssignWorkOrder = async (workOrderId) => {
    setAssigningWorkOrder(true);
    try {
      const updated = await formSubmissionService.assignWorkOrder(submission.id, workOrderId);
      setSubmission(prev => ({ ...prev, workOrderId: updated.workOrderId }));
      if (workOrderId) {
        const wo = availableWorkOrders.find(w => w.id === workOrderId);
        setLinkedWorkOrder(wo || null);
        if (!wo) {
          try {
            const fetched = await workOrderService.getWorkOrderById(workOrderId);
            setLinkedWorkOrder(fetched);
          } catch (e) { /* ignore */ }
        }
        toast.success('Work order assigned');
      } else {
        setLinkedWorkOrder(null);
        toast.success('Work order removed');
      }
      setWoModalOpen(false);
    } catch (err) {
      console.error('Failed to assign work order:', err);
      toast.error('Failed to assign work order');
    } finally {
      setAssigningWorkOrder(false);
    }
  };

  const filteredWorkOrders = availableWorkOrders.filter(wo => {
    if (!woSearchTerm) return true;
    const term = woSearchTerm.toLowerCase();
    return (
      (wo.title || '').toLowerCase().includes(term) ||
      (wo.workOrderNumber || '').toLowerCase().includes(term) ||
      (wo.customerName || '').toLowerCase().includes(term) ||
      (wo.locationName || '').toLowerCase().includes(term)
    );
  });

  /**
   * Derives a meaningful label for a child instance from its first non-empty field value.
   * Falls back to "{displayName} #{index + 1}" when no meaningful data is found.
   */
  const getChildInstanceLabel = (childFields, fieldKeys, index, displayName) => {
    const fallback = `${displayName} #${index + 1}`;
    if (!childFields || !fieldKeys || fieldKeys.length === 0) return fallback;

    for (const key of fieldKeys) {
      const value = childFields[key];
      if (value !== null && value !== undefined && value !== '') {
        const stringValue = String(value).trim();
        if (stringValue.length > 0 && stringValue !== 'false' && !stringValue.startsWith('[object')) {
          const label = stringValue.length > 60 ? `${stringValue.substring(0, 60)}...` : stringValue;
          return `#${index + 1} - ${label}`;
        }
      }
    }

    return fallback;
  };

  const handleImageClick = (attachment) => {
    setSelectedImage(attachment);
    setImageModalOpen(true);
  };

  const renderFieldValue = (field, value) => {
    if (value === null || value === undefined || value === '') {
      return <ThemedText style={{ color: theme.colors.muted }}>Not provided</ThemedText>;
    }

    // Delegate to registry-driven renderer for all non-repeated_form fields
    if (field.type !== 'repeated_form') {
      return renderDetailValue(field, value, {
        timezone: effectiveTimezone,
        theme,
        attachments: submission?.attachments,
        onImageClick: handleImageClick,
        fieldColor: ruleFieldStyles[field.id] || null,
      });
    }

    // repeated_form — complex local state interactions, kept inline
    {
        // The parent stores the IDs of children that belong to this specific
        // field at submissionData.fields[field.key] (see API
        // FormSubmissionService line 1333). Use that as the source of truth so
        // that fields with mismatched/missing repeatedFormSchemaId still
        // resolve correctly under the new "any form as repeated" capability.
        const fieldSchemaId = field.props?.configuration?.repeatedFormSchemaId;
        const childIdSet = Array.isArray(value)
          ? new Set(value.map(Number))
          : null;
        const childSubmissions = (submission?.childSubmissions || [])
          .filter(c => {
            if (childIdSet && childIdSet.size > 0) return childIdSet.has(Number(c.id));
            // Legacy fallback: schema-ID match with type coercion.
            return fieldSchemaId && Number(c.formSchemaId) === Number(fieldSchemaId);
          });

        if (childSubmissions.length === 0) {
          const emptyConfig = field.props.configuration || {};
          const emptyAllowLink = emptyConfig.allowLinkExisting !== false;
          const emptyTargetSchemaId = emptyConfig.repeatedFormSchemaId;
          return (
            <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.md }}>
              <ThemedText style={{ color: theme.colors.muted }}>No instances</ThemedText>
              {!isEditMode && emptyAllowLink && emptyTargetSchemaId && (
                <button
                  onClick={() => setLinkPicker({ open: true, field })}
                  style={{
                    display: 'inline-flex', alignItems: 'center', gap: 6,
                    padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                    fontSize: 13, fontWeight: 500, color: theme.colors.primary,
                    background: 'transparent', border: `1px solid ${theme.colors.border}`,
                    borderRadius: theme.borderRadius.md, cursor: 'pointer',
                    textTransform: 'none',
                  }}
                >
                  <Link2 size={14} /> Link existing
                </button>
              )}
            </div>
          );
        }

        // Sort child submissions by instanceIndex
        const sortedChildren = [...childSubmissions].sort((a, b) =>
          (a.instanceIndex || 0) - (b.instanceIndex || 0)
        );

        const config = field.props.configuration || {};
        const displayName = config.repeatedFormDisplayName || 'Instance';
        // Force list view in edit mode — table/grid don't support inline editing
        const viewMode = isEditMode ? 'list' : (childViewModes[field.id] || 'list');

        const toggleChildCollapse = (childId) => {
          setCollapsedChildren(prev => ({ ...prev, [childId]: !prev[childId] }));
        };

        const setViewMode = (mode) => {
          setChildViewModes(prev => ({ ...prev, [field.id]: mode }));
        };

        // Render a child field value; editable in edit mode for non-attachment fields
        const renderChildFieldValue = (child, fieldKey, fieldValue) => {
          const childFieldDef = childFieldSchemas[field.id]?.[fieldKey];
          const childFieldType = childFieldDef?.type;
          // Stamp the data key as `id` so image/attachment renderers can match an
          // attachment by fieldName (childFieldDef carries no id/key of its own).
          const childField = { ...(childFieldDef || {}), id: fieldKey, type: childFieldType || childFieldDef?.type || 'input', props: childFieldDef?.props || {} };

          // Edit mode: media/location fields are read-only, text fields get inline input
          if (isEditMode) {
            const isMedia = childFieldDef && isMediaType(childFieldType);
            const isLocation = childFieldType === 'geolocation' || childFieldType === 'location';
            if (!isMedia && !isLocation) {
              const currentVal = editedChildFields[child.id]?.[fieldKey] ?? fieldValue;
              return (
                <ChildEditInput
                  type="text"
                  value={currentVal === null || currentVal === undefined ? '' : String(currentVal)}
                  theme={theme}
                  onChange={(e) => handleChildFieldChange(child.id, fieldKey, e.target.value)}
                />
              );
            }
          }

          if (fieldValue === null || fieldValue === undefined || fieldValue === '') {
            return <ThemedText style={{ color: theme.colors.muted }}>-</ThemedText>;
          }

          // Registry-driven rendering for all child field types
          return renderDetailValue(childField, fieldValue, {
            timezone: effectiveTimezone,
            theme,
            attachments: child?.attachments,
            onImageClick: handleImageClick,
          });
        };

        // Build field keys in schema-defined order (not arbitrary data key order)
        const schemaFieldKeys = childFieldSchemas[field.id]
          ? Object.keys(childFieldSchemas[field.id])
          : [];
        const extraDataKeys = new Set();
        sortedChildren.forEach(child => {
          const childFields = formSubmissionService.getSubmissionFields(child.submissionData);
          Object.keys(childFields).forEach(key => {
            if (!schemaFieldKeys.includes(key)) extraDataKeys.add(key);
          });
        });
        const fieldKeys = [...schemaFieldKeys, ...Array.from(extraDataKeys)];

        const allowLinkExisting = config.allowLinkExisting !== false;
        const targetSchemaId = config.repeatedFormSchemaId;

        return (
          <ChildSubmissionsContainer theme={theme}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: theme.spacing.md, gap: theme.spacing.md }}>
              <ThemedText type="defaultSemiBold">
                {sortedChildren.length} {displayName}{sortedChildren.length !== 1 ? 's' : ''}
              </ThemedText>

              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                {!isEditMode && allowLinkExisting && targetSchemaId && (
                  <button
                    onClick={() => setLinkPicker({ open: true, field })}
                    title={`Link an existing ${displayName.toLowerCase()} to this submission`}
                    style={{
                      display: 'inline-flex', alignItems: 'center', gap: 6,
                      padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                      fontSize: theme.typography.sizes.small, fontWeight: 500,
                      color: theme.colors.primary,
                      background: 'transparent', border: `1px solid ${theme.colors.border}`,
                      borderRadius: theme.borderRadius.md, cursor: 'pointer',
                      textTransform: 'none',
                    }}
                  >
                    <Link2 size={14} /> Link existing
                  </button>
                )}

                {!isEditMode && (
                  <ViewSelector theme={theme} style={{ marginBottom: 0 }}>
                    <ViewButton
                      theme={theme}
                      isActive={viewMode === 'list'}
                      onClick={() => setViewMode('list')}
                      title="List view"
                    >
                      <List size={14} /> List
                    </ViewButton>
                    <ViewButton
                      theme={theme}
                      isActive={viewMode === 'table'}
                      onClick={() => setViewMode('table')}
                      title="Table view"
                    >
                      <Table2 size={14} /> Table
                    </ViewButton>
                    <ViewButton
                      theme={theme}
                      isActive={viewMode === 'grid'}
                      onClick={() => setViewMode('grid')}
                      title="Grid view"
                    >
                      <Grid3x3 size={14} /> Grid
                    </ViewButton>
                  </ViewSelector>
                )}
              </div>
            </div>

            {/* List View (Original collapsible cards) */}
            {viewMode === 'list' && sortedChildren.map((child, index) => {
              const childFields = formSubmissionService.getSubmissionFields(child.submissionData);
              // Keep all cards expanded in edit mode so fields are visible
              const isCollapsed = isEditMode ? false : collapsedChildren[child.id];

              return (
                <ChildSubmissionCard key={child.id} theme={theme}>
                  <ChildSubmissionHeader
                    theme={theme}
                    onClick={() => toggleChildCollapse(child.id)}
                  >
                    <ThemedText type="defaultSemiBold">
                      {getChildInstanceLabel(childFields, fieldKeys, index, displayName)}
                      {isCollapsed ? <> <ChevronRight size={14} /></> : <> <ChevronDown size={14} /></>}
                    </ThemedText>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      {formatDate(child.submittedAt || child.createdAt)}
                    </ThemedText>
                  </ChildSubmissionHeader>

                  <ChildSubmissionBody theme={theme} isCollapsed={isCollapsed}>
                    {fieldKeys.map(fieldKey => {
                      const fieldValue = childFields[fieldKey];
                      if (fieldValue === undefined) return null;
                      return (
                        <FieldRow key={fieldKey} theme={theme}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                            {childFieldSchemas[field.id]?.[fieldKey] &&
                              renderFieldTypeIcon(childFieldSchemas[field.id][fieldKey].type, 12, { color: theme.colors.muted })}
                            <FieldLabel theme={theme}>{childFieldSchemas[field.id]?.[fieldKey]?.label || fieldKey}</FieldLabel>
                          </div>
                          <FieldValue>
                            {renderChildFieldValue(child, fieldKey, fieldValue)}
                          </FieldValue>
                        </FieldRow>
                      );
                    }).filter(Boolean)}

                    {Object.keys(childFields).length === 0 && (
                      <ThemedText style={{ color: theme.colors.muted }}>
                        No data available
                      </ThemedText>
                    )}
                  </ChildSubmissionBody>
                </ChildSubmissionCard>
              );
            })}

            {/* Table View */}
            {viewMode === 'table' && (
              <TableContainer theme={theme}>
                <StyledTable theme={theme}>
                  <thead>
                    <tr>
                      <th>#</th>
                      {fieldKeys.map(key => (
                        <th key={key}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: '4px' }}>
                            {childFieldSchemas[field.id]?.[key] &&
                              renderFieldTypeIcon(childFieldSchemas[field.id][key].type, 12)}
                            {childFieldSchemas[field.id]?.[key]?.label || key}
                          </span>
                        </th>
                      ))}
                      <th>Submitted</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sortedChildren.map((child, index) => {
                      const childFields = formSubmissionService.getSubmissionFields(child.submissionData);

                      return (
                        <tr key={child.id}>
                          <td>
                            <strong>{index + 1}</strong>
                          </td>
                          {fieldKeys.map(key => (
                            <td key={key}>
                              {renderChildFieldValue(child, key, childFields[key])}
                            </td>
                          ))}
                          <td>
                            <ThemedText type="small" style={{ color: theme.colors.muted }}>
                              {formatDate(child.submittedAt || child.createdAt)}
                            </ThemedText>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </StyledTable>
              </TableContainer>
            )}

            {/* Grid View */}
            {viewMode === 'grid' && (
              <GridContainer theme={theme}>
                {sortedChildren.map((child, index) => {
                  const childFields = formSubmissionService.getSubmissionFields(child.submissionData);

                  return (
                    <GridCard key={child.id} theme={theme}>
                      <GridCardHeader theme={theme}>
                        {(() => {
                          const gridChildFields = formSubmissionService.getSubmissionFields(child.submissionData);
                          return getChildInstanceLabel(gridChildFields, fieldKeys, index, displayName);
                        })()}
                      </GridCardHeader>

                      {fieldKeys.map(fieldKey => {
                        const fieldValue = childFields[fieldKey];
                        if (fieldValue === undefined) return null;
                        return (
                          <FieldRow key={fieldKey} theme={theme}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                              {childFieldSchemas[field.id]?.[fieldKey] &&
                                renderFieldTypeIcon(childFieldSchemas[field.id][fieldKey].type, 12, { color: theme.colors.muted })}
                              <FieldLabel theme={theme}>{childFieldSchemas[field.id]?.[fieldKey]?.label || fieldKey}</FieldLabel>
                            </div>
                            <FieldValue>
                              {renderChildFieldValue(child, fieldKey, fieldValue)}
                            </FieldValue>
                          </FieldRow>
                        );
                      }).filter(Boolean)}

                      <ThemedText type="small" style={{ color: theme.colors.muted, marginTop: theme.spacing.sm }}>
                        {formatDate(child.submittedAt || child.createdAt)}
                      </ThemedText>

                      {Object.keys(childFields).length === 0 && (
                        <ThemedText style={{ color: theme.colors.muted }}>
                          No data available
                        </ThemedText>
                      )}
                    </GridCard>
                  );
                })}
              </GridContainer>
            )}
          </ChildSubmissionsContainer>
        );
    }
  };

  // Determine if the current user can edit this submission
  const canEdit = useMemo(() => {
    if (!submission) return false;
    if (readOnly) return false; // read-only demo viewer — all edits frozen (backend enforces too)
    const nonEditableStatuses = ['Approved', 'Rejected', 'Archived'];
    if (nonEditableStatuses.includes(submission.status)) return false;
    if (isSuperAdmin() || isAdmin()) return true;
    return submission.submitterEmail?.toLowerCase() === user?.email?.toLowerCase();
  }, [submission, isSuperAdmin, isAdmin, user, readOnly]);

  // Whether the location pin is currently editable, and the effective coordinates (pending edits
  // override the stored values). Used by the location card below.
  const editingLocation = canEdit && isEditMode && allowEditSubmissionLocation;
  const effLat = editedMeta.latitude != null ? editedMeta.latitude : submission?.latitude;
  const effLng = editedMeta.longitude != null ? editedMeta.longitude : submission?.longitude;

  const handleFieldChange = (key, val) => {
    setEditedFields(prev => ({ ...prev, [key]: val }));
  };

  const handleChildFieldChange = (childId, fieldKey, val) => {
    setEditedChildFields(prev => ({
      ...prev,
      [childId]: { ...(prev[childId] || {}), [fieldKey]: val },
    }));
  };

  const handleCancelEdit = () => {
    setIsEditMode(false);
    setEditedFields({});
    setEditedChildFields({});
    setEditedMeta({});
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Save parent submission. Metadata overrides (location / datetime / submitter) ride alongside
      // the field data; the server applies each only when the org enables the matching flag.
      const merged = { ...submissionData, ...editedFields };
      const meta = {};
      if (editedMeta.latitude != null) meta.latitude = editedMeta.latitude;
      if (editedMeta.longitude != null) meta.longitude = editedMeta.longitude;
      if (editedMeta.locationName != null) meta.locationName = editedMeta.locationName;
      if (editedMeta.submittedAtLocal) {
        const iso = new Date(editedMeta.submittedAtLocal).toISOString();
        if (!Number.isNaN(Date.parse(iso))) meta.submittedAt = iso;
      }
      if (editedMeta.submitterName != null) meta.submitterName = editedMeta.submitterName;
      if (editedMeta.submitterEmail != null) meta.submitterEmail = editedMeta.submitterEmail;
      await formSubmissionService.updateSubmission(submission.id, { fields: merged }, meta);

      // Save each modified child submission
      const childEditEntries = Object.entries(editedChildFields);
      if (childEditEntries.length > 0) {
        await Promise.all(childEditEntries.map(async ([childIdStr, childEdits]) => {
          const childId = parseInt(childIdStr, 10);
          const child = submission.childSubmissions?.find(c => c.id === childId);
          if (!child) return;
          const currentChildFields = formSubmissionService.getSubmissionFields(child.submissionData);
          const mergedChildFields = { ...currentChildFields, ...childEdits };
          await formSubmissionService.updateSubmission(childId, { fields: mergedChildFields });
        }));
      }

      toast.success('Submission updated successfully');
      setIsEditMode(false);
      setEditedFields({});
      setEditedChildFields({});
      setEditedMeta({});
      // Re-fetch the submission to reflect updated data
      const refreshed = await formSubmissionService.getSubmissionById(submissionId, { includeChildren: true });
      setSubmission(refreshed);
      const fieldValues = formSubmissionService.getSubmissionFields(refreshed.submissionData);
      setSubmissionData(fieldValues || {});
    } catch (err) {
      console.error('Failed to save submission edit:', err);
      // The axios interceptor wraps errors as { message, status, data, originalError }
      const responseData = err.data;
      if (responseData?.errors && Array.isArray(responseData.errors)) {
        toast.error(responseData.message || 'Submission validation failed');
        responseData.errors.forEach(e => toast.error(e.message));
      } else {
        toast.error(responseData?.message || err.message || 'Failed to save changes');
      }
    } finally {
      setIsSaving(false);
    }
  };

  const handleBack = () => {
    navigate(`/forms/${formId}/submissions`);
  };

  // PDF generation handler
  if (loading) {
    return (
      <Layout currentPath={`/forms/${formId}/submissions/${submissionId}`}>
        <LoadingContainer theme={theme}>
          <ThemedText>Loading submission...</ThemedText>
        </LoadingContainer>
      </Layout>
    );
  }

  if (!submission) {
    return (
      <Layout currentPath={`/forms/${formId}/submissions/${submissionId}`}>
        <PageContainer theme={theme}>
          <ThemedText type="title">Submission not found</ThemedText>
          <Button onClick={handleBack} style={{ marginTop: theme.spacing.lg }}>
            <ArrowLeftIcon size={14} /> Back to Submissions
          </Button>
        </PageContainer>
      </Layout>
    );
  }

  return (
    <Layout currentPath={`/forms/${formId}/submissions/${submissionId}`}>
      <Helmet>
        <title>{`Submission #${submission.id} - ${formSchema?.name || 'Form'} - aQRate Portal`}</title>
        <meta name="description" content={`View submission #${submission.id}`} />
      </Helmet>

      <PageContainer theme={theme}>
        <PageHeader
          theme={theme}
          title={`Submission #${submission.id}`}
          subtitle={formSchema?.name || 'Form Submission'}
          icon={<Pencil size={20} />}
          onBack={handleBack}
          backLabel="Back to List"
        />

        <ContentGrid theme={theme}>
          <MainContent theme={theme}>
            <ViewSelector theme={theme}>
              <ViewButton
                theme={theme}
                isActive={activeView === 'form'}
                onClick={() => setActiveView('form')}
              >
                Form View
              </ViewButton>
              <ViewButton
                theme={theme}
                isActive={activeView === 'history'}
                onClick={() => { setActiveView('history'); loadAuditHistory(); }}
              >
                History
              </ViewButton>
              <ViewButton
                theme={theme}
                isActive={activeView === 'lab-reports'}
                onClick={() => { setActiveView('lab-reports'); loadLabReports(); }}
              >
                Lab Reports
              </ViewButton>
              <ViewButton
                theme={theme}
                isActive={activeView === 'comments'}
                onClick={() => { setActiveView('comments'); loadComments(); }}
              >
                <span style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                  <MessageCircle size={14} />
                  Comments{comments.length > 0 ? ` (${comments.length})` : ''}
                  {unreadCommentCount > 0 && activeView !== 'comments' && (
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      minWidth: '18px',
                      height: '18px',
                      padding: '0 5px',
                      borderRadius: '9px',
                      backgroundColor: '#dc3545',
                      color: 'white',
                      fontSize: '10px',
                      fontWeight: 700,
                      lineHeight: 1,
                    }}>
                      {unreadCommentCount}
                    </span>
                  )}
                </span>
              </ViewButton>
            </ViewSelector>

            {activeView === 'form' && (
            <Card theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Form Data</ThemedText>
                <div style={{ display: 'flex', gap: theme.spacing.sm, alignItems: 'center' }}>
                  {!isEditMode && (
                    <CheckboxLabel theme={theme}>
                      <input
                        type="checkbox"
                        checked={hideEmptyFields}
                        onChange={(e) => setHideEmptyFields(e.target.checked)}
                      />
                      <span>Hide unfilled fields</span>
                    </CheckboxLabel>
                  )}
                  {canEdit && !isEditMode && (
                    <Button
                      variant="secondary"
                      size="sm"
                      onClick={() => { setEditedFields({}); setEditedMeta({}); setIsEditMode(true); }}
                    >
                      Edit Submission
                    </Button>
                  )}
                </div>
              </CardHeader>

              <ReadOnlyFormRenderer
                schema={formFields.filter(f => f.type !== 'repeated_form')}
                formData={isEditMode ? { ...submissionData, ...editedFields } : submissionData}
                attachments={submission?.attachments || []}
                hideEmptyFields={isEditMode ? false : hideEmptyFields}
                theme={theme}
                isEditable={isEditMode}
                onFieldChange={handleFieldChange}
                onImageClick={(att) => {
                  setSelectedImage(att);
                  setImageModalOpen(true);
                }}
                onLocationClick={() => {
                  setLocationModalOpen(true);
                }}
              />

              {/* Save / Cancel actions in edit mode */}
              {isEditMode && (
                <EditActionsBar theme={theme}>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? 'Saving...' : 'Save Changes'}
                  </Button>
                  <Button
                    variant="secondary"
                    onClick={handleCancelEdit}
                    disabled={isSaving}
                  >
                    Cancel
                  </Button>
                </EditActionsBar>
              )}

              {/* Render repeated form fields (editable in edit mode via renderFieldValue) */}
              {formFields
                .filter(field => field.type === 'repeated_form' && !ruleHiddenKeySet.has(field.id))
                .map((field) => (
                  <div key={field.id} style={{ marginTop: theme.spacing.lg }}>
                    <ReadOnlyLabel theme={theme}>
                      {field.props.label}
                      {field.props.required && ' *'}
                    </ReadOnlyLabel>
                    {renderFieldValue(field, submissionData[field.id])}
                  </div>
                ))
              }
            </Card>
            )}

            {activeView === 'history' && (
              <Card theme={theme}>
                <CardHeader theme={theme}>
                  <span style={{ fontWeight: 600 }}>Change History</span>
                </CardHeader>
                {historyLoading ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textSecondary }}>Loading history...</div>
                ) : auditHistory.length === 0 ? (
                  <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.textSecondary }}>No change history found for this submission.</div>
                ) : (
                  <div style={{ padding: '0' }}>
                    {auditHistory.map((log) => (
                      <div
                        key={log.id}
                        onClick={() => log.detailCount > 0 ? loadAuditDetail(log.id) : null}
                        style={{
                          padding: '12px 16px',
                          borderBottom: `1px solid ${theme.colors.border}`,
                          cursor: log.detailCount > 0 ? 'pointer' : 'default',
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          transition: 'background-color 0.15s',
                        }}
                        onMouseEnter={(e) => { if (log.detailCount > 0) e.currentTarget.style.backgroundColor = theme.colors.backgroundSecondary; }}
                        onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; }}
                      >
                        <div style={{ flex: 1 }}>
                          <div style={{ fontWeight: 500, fontSize: '14px', marginBottom: '4px' }}>
                            <span style={{
                              display: 'inline-block', padding: '2px 8px', borderRadius: '4px', fontSize: '12px', fontWeight: 600, marginRight: '8px',
                              backgroundColor: log.action === 'Create' ? '#dcfce7' : log.action === 'Update' ? '#dbeafe' : log.action === 'Delete' ? '#fecaca' : log.action === 'StatusChange' ? '#fef3c7' : '#f3f4f6',
                              color: log.action === 'Create' ? '#166534' : log.action === 'Update' ? '#1e40af' : log.action === 'Delete' ? '#991b1b' : log.action === 'StatusChange' ? '#92400e' : '#374151',
                            }}>
                              {log.action}
                            </span>
                            {log.description || log.action}
                          </div>
                          <div style={{ fontSize: '12px', color: theme.colors.textSecondary }}>
                            {log.userEmail || 'System'} &middot; {new Date(log.timestamp).toLocaleString()}
                          </div>
                        </div>
                        {log.detailCount > 0 && (
                          <span style={{
                            backgroundColor: theme.colors.tint, color: 'white', borderRadius: '12px',
                            padding: '2px 8px', fontSize: '12px', fontWeight: 600, minWidth: '24px', textAlign: 'center'
                          }}>
                            {log.detailCount}
                          </span>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Audit Detail Expand */}
                {selectedAuditLog && auditDetail && (
                  <div style={{ padding: '16px', backgroundColor: theme.colors.backgroundSecondary, borderTop: `2px solid ${theme.colors.border}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                      <span style={{ fontWeight: 600, fontSize: '14px' }}>Field Changes</span>
                      <button onClick={() => { setSelectedAuditLog(null); setAuditDetail(null); }} style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '16px', color: theme.colors.textSecondary, display: 'flex', alignItems: 'center' }}><X size={14} /></button>
                    </div>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                      <thead>
                        <tr>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${theme.colors.border}`, fontWeight: 600 }}>Field</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${theme.colors.border}`, fontWeight: 600 }}>Old Value</th>
                          <th style={{ textAlign: 'left', padding: '8px', borderBottom: `1px solid ${theme.colors.border}`, fontWeight: 600 }}>New Value</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(auditDetail.details || []).map((detail) => {
                          // Try to resolve field key to human-readable title from form schema
                          const schemaFields = formSchema?.schemaDefinition?.fields || formFields || [];
                          const matchedField = schemaFields.find(f => (f.key || f.id) === detail.fieldName);
                          const displayName = matchedField?.title || matchedField?.props?.name || detail.fieldName;

                          return (
                            <tr key={detail.id}>
                              <td style={{ padding: '8px', borderBottom: `1px solid ${theme.colors.border}`, fontWeight: 500 }}>{displayName}</td>
                              <td style={{ padding: '8px', borderBottom: `1px solid ${theme.colors.border}`, backgroundColor: detail.oldValue ? 'rgba(239,68,68,0.07)' : 'transparent', maxWidth: '300px', wordBreak: 'break-word' }}>{detail.oldValue || '\u2014'}</td>
                              <td style={{ padding: '8px', borderBottom: `1px solid ${theme.colors.border}`, backgroundColor: detail.newValue ? 'rgba(34,197,94,0.07)' : 'transparent', maxWidth: '300px', wordBreak: 'break-word' }}>{detail.newValue || '\u2014'}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </Card>
            )}

            {activeView === 'lab-reports' && (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                {/* Linked Lab Samples */}
                <Card theme={theme}>
                  <CardHeader theme={theme}>
                    <span style={{ fontWeight: 600 }}>
                      Linked Lab Samples
                      {linkedLabSamples.length > 0 && (
                        <span style={{ fontWeight: 400, fontSize: 13, color: theme.colors.muted, marginLeft: 8 }}>
                          ({linkedLabSamples.length})
                        </span>
                      )}
                    </span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <button
                        onClick={handleAutoMatchSubmission}
                        disabled={autoMatchLoading}
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 500,
                          border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md,
                          backgroundColor: theme.colors.background, color: theme.colors.text, cursor: 'pointer',
                          opacity: autoMatchLoading ? 0.6 : 1,
                        }}
                      >
                        {autoMatchLoading ? 'Matching...' : 'Auto-Match'}
                      </button>
                      <button
                        onClick={loadLabReports}
                        disabled={labSamplesLoading}
                        style={{
                          padding: '6px 12px', fontSize: 12, fontWeight: 500,
                          border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.md,
                          backgroundColor: theme.colors.background, color: theme.colors.muted, cursor: 'pointer',
                        }}
                      >
                        Refresh
                      </button>
                    </div>
                  </CardHeader>
                  {labSamplesLoading ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.muted }}>Loading lab samples...</div>
                  ) : linkedLabSamples.length === 0 ? (
                    <div style={{ padding: '24px', textAlign: 'center', color: theme.colors.muted }}>
                      <div style={{ marginBottom: 8 }}>No lab samples linked to this submission.</div>
                      <div style={{ fontSize: 12 }}>
                        Click "Auto-Match" to find lab samples matching this submission's Sample ID, or link them from the Lab Report detail page.
                      </div>
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
                        <thead>
                          <tr style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 11, textTransform: 'uppercase' }}>Report</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 11, textTransform: 'uppercase' }}>Sample Ref</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 11, textTransform: 'uppercase' }}>Description</th>
                            <th style={{ padding: '8px 12px', textAlign: 'left', fontWeight: 600, color: theme.colors.muted, fontSize: 11, textTransform: 'uppercase' }}>Match Reason</th>
                            <th style={{ padding: '8px 12px', textAlign: 'right', fontWeight: 600, color: theme.colors.muted, fontSize: 11, textTransform: 'uppercase' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {linkedLabSamples.map(s => (
                            <tr key={s.labSampleId} style={{ borderBottom: `1px solid ${theme.colors.border}` }}>
                              <td style={{ padding: '8px 12px' }}>
                                <span
                                  onClick={() => navigate(`/lab-reports/${s.labReportId}`)}
                                  style={{ color: theme.colors.primary, cursor: 'pointer', fontWeight: 500 }}
                                >
                                  {s.reportNumber || s.originalFileName || `Report #${s.labReportId}`}
                                </span>
                                {s.labName && <div style={{ fontSize: 11, color: theme.colors.muted }}>{s.labName}</div>}
                              </td>
                              <td style={{ padding: '8px 12px', fontFamily: 'monospace', fontSize: 12 }}>
                                {s.referenceNumber}
                                {s.isChildLink && s.linkedSampleIdValue && (
                                  <div style={{ fontSize: 10, color: theme.colors.muted, fontFamily: 'inherit', marginTop: 2 }}>
                                    via {s.linkedSampleIdValue}
                                  </div>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px', color: theme.colors.muted }}>{s.sampleInfo || '-'}</td>
                              <td style={{ padding: '8px 12px' }}>
                                {s.matchReason && (
                                  <span style={{
                                    padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 500,
                                    backgroundColor: theme.colors.backgroundSecondary, color: theme.colors.muted,
                                  }}>
                                    {s.matchReason}
                                  </span>
                                )}
                              </td>
                              <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                                <button
                                  onClick={() => handleUnlinkSample(s.labReportId, s.labSampleId)}
                                  style={{
                                    padding: '4px 10px', fontSize: 11, fontWeight: 500,
                                    border: `1px solid ${theme.colors.border}`, borderRadius: theme.borderRadius.sm,
                                    backgroundColor: 'transparent', color: theme.colors.danger || '#e74c3c', cursor: 'pointer',
                                  }}
                                >
                                  Unlink
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </Card>

                {/* Linked Lab Reports */}
                {submissionLabReports.length > 0 && (
                  <Card theme={theme}>
                    <CardHeader theme={theme}>
                      <span style={{ fontWeight: 600 }}>Linked Lab Reports</span>
                    </CardHeader>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                      {submissionLabReports.map(r => (
                        <div
                          key={r.id}
                          onClick={() => navigate(`/lab-reports/${r.id}`)}
                          style={{
                            padding: '12px 16px',
                            cursor: 'pointer',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            borderRadius: theme.borderRadius.md,
                            border: `1px solid ${theme.colors.border}`,
                            backgroundColor: theme.colors.background,
                          }}
                        >
                          <div>
                            <div style={{ fontWeight: 600, fontSize: 14 }}>{r.reportNumber || r.originalFileName}</div>
                            <div style={{ fontSize: 12, color: theme.colors.muted, marginTop: 2 }}>
                              {r.labName && <span>{r.labName} · </span>}
                              {r.dateReported ? new Date(r.dateReported).toLocaleDateString() : ''}
                              {r.sampleCount > 0 && <span> · {r.sampleCount} sample{r.sampleCount !== 1 ? 's' : ''}</span>}
                            </div>
                          </div>
                          <span style={{
                            padding: '3px 10px', borderRadius: 10, fontSize: 12, fontWeight: 500,
                            backgroundColor: LabReportStatusColors[r.status] ? LabReportStatusColors[r.status] + '22' : theme.colors.backgroundSecondary,
                            color: LabReportStatusColors[r.status] || theme.colors.muted,
                          }}>
                            {getLabReportStatusLabel(r.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  </Card>
                )}
              </div>
            )}

            {activeView === 'comments' && (
              <Card theme={theme}>
                <CardHeader theme={theme}>
                  <ThemedText type="subtitle">Comments</ThemedText>
                  {comments.length > 0 && (
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      {comments.filter(c => !c.isSystemGenerated).length} comment{comments.filter(c => !c.isSystemGenerated).length !== 1 ? 's' : ''}
                    </ThemedText>
                  )}
                </CardHeader>

                {commentsLoading ? (
                  <div style={{ display: 'flex', justifyContent: 'center', padding: theme.spacing.xl }}>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>Loading comments...</ThemedText>
                  </div>
                ) : comments.length === 0 ? (
                  <CommentEmptyState theme={theme}>
                    <MessageCircle size={32} strokeWidth={1.5} />
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      No comments yet
                    </ThemedText>
                    <ThemedText type="small" style={{ color: theme.colors.muted, fontSize: '12px' }}>
                      Start a conversation about this submission
                    </ThemedText>
                  </CommentEmptyState>
                ) : (
                  <CommentsList theme={theme}>
                    {comments.map((comment) => (
                      comment.isSystemGenerated ? (
                        <SystemCommentItem key={comment.id} theme={theme}>
                          <span style={{ width: 6, height: 6, borderRadius: '50%', backgroundColor: theme.colors.muted, flexShrink: 0 }} />
                          <span style={{ flex: 1 }}>{comment.content}</span>
                          <CommentTime theme={theme} style={{ flexShrink: 0 }}>
                            {formatSubmissionDate(comment.createdAt, effectiveTimezone)}
                          </CommentTime>
                        </SystemCommentItem>
                      ) : (
                        <CommentItem key={comment.id} theme={theme}>
                          <Avatar
                            name={comment.createdByUserName || 'Unknown'}
                            src={comment.createdByUserProfilePicture}
                            size="sm"
                          />
                          <CommentBubble theme={theme}>
                            <CommentHeader theme={theme}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm }}>
                                <CommentAuthor theme={theme}>{comment.createdByUserName || 'Unknown'}</CommentAuthor>
                                <CommentTime theme={theme}>
                                  {formatSubmissionDate(comment.createdAt, effectiveTimezone)}
                                </CommentTime>
                              </div>
                              {(isSuperAdmin() || isAdmin()) && (
                                <DeleteCommentButton
                                  theme={theme}
                                  onClick={() => handleDeleteComment(comment.id)}
                                  title="Delete comment"
                                >
                                  <Trash2 size={13} />
                                </DeleteCommentButton>
                              )}
                            </CommentHeader>
                            <CommentText theme={theme}>{comment.content}</CommentText>
                          </CommentBubble>
                        </CommentItem>
                      )
                    ))}
                  </CommentsList>
                )}

                <CommentInputArea theme={theme}>
                  <CommentTextarea
                    theme={theme}
                    value={newComment}
                    onChange={(e) => setNewComment(e.target.value)}
                    placeholder="Write a comment..."
                    rows={1}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
                        handleAddComment();
                      }
                    }}
                    onInput={(e) => {
                      e.target.style.height = 'auto';
                      e.target.style.height = Math.min(e.target.scrollHeight, 120) + 'px';
                    }}
                  />
                  <SendButton
                    theme={theme}
                    onClick={handleAddComment}
                    disabled={!newComment.trim() || submittingComment}
                    title={submittingComment ? 'Sending...' : 'Send (Ctrl+Enter)'}
                  >
                    <Send size={16} />
                  </SendButton>
                </CommentInputArea>
              </Card>
            )}
          </MainContent>

          <Sidebar theme={theme}>
            {/* Generate PDF Report Card */}
            <Card theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Generate Report</ThemedText>
              </CardHeader>
              {loadingTemplates ? (
                <ThemedText type="small">Loading templates...</ThemedText>
              ) : pdfTemplates.length === 0 ? (
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  No report templates available for this form.
                </ThemedText>
              ) : (
                <>
                  {pdfTemplates.length > 1 && (
                    <TemplateSelect
                      theme={theme}
                      value={selectedTemplateId || ''}
                      onChange={(e) => setSelectedTemplateId(Number(e.target.value))}
                    >
                      {pdfTemplates.map(t => (
                        <option key={t.id} value={t.id}>
                          {t.name} {t.isDefault ? '(Default)' : ''}
                        </option>
                      ))}
                    </TemplateSelect>
                  )}
                  {pdfTemplates.length === 1 && (
                    <ThemedText type="small" style={{ marginBottom: theme.spacing.md }}>
                      Template: {pdfTemplates[0].name}
                    </ThemedText>
                  )}
                  <Button
                    onClick={handleServerGeneratePdf}
                    disabled={generatingServerPdf || !selectedTemplateId}
                    style={{ width: '100%' }}
                  >
                    <Cloud size={16} style={{ marginRight: theme.spacing.xs }} />
                    {generatingServerPdf ? 'Queuing...' : 'Generate PDF'}
                  </Button>
                </>
              )}
            </Card>

            {/* Standalone (Preact+htm) visualization — compact gadget; opens the full default viz in a modal.
                Public share links now live INSIDE that modal (see VizShareLinks), so there is no separate card. */}
            <StandaloneVizSubmissionCard
              formSchemaId={parseInt(formId, 10)}
              submissionId={submission.id}
            />

            {/* Generated Reports */}
            <Card theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Generated Reports</ThemedText>
                <button
                  onClick={loadGeneratedReports}
                  disabled={loadingReports}
                  title="Refresh reports"
                  style={{
                    background: 'none',
                    border: `1px solid ${theme.colors.border}`,
                    borderRadius: '6px',
                    cursor: loadingReports ? 'not-allowed' : 'pointer',
                    padding: '4px 8px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    color: theme.colors.muted,
                    fontSize: '0.75rem',
                    opacity: loadingReports ? 0.5 : 1,
                    transition: 'color 0.15s, border-color 0.15s',
                  }}
                  onMouseEnter={e => { e.currentTarget.style.color = theme.colors.primary; e.currentTarget.style.borderColor = theme.colors.primary; }}
                  onMouseLeave={e => { e.currentTarget.style.color = theme.colors.muted; e.currentTarget.style.borderColor = theme.colors.border; }}
                >
                  {loadingReports ? (
                    <SpinIcon width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </SpinIcon>
                  ) : (
                    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }}>
                      <polyline points="23 4 23 10 17 10" />
                      <polyline points="1 20 1 14 7 14" />
                      <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15" />
                    </svg>
                  )}
                  {loadingReports ? 'Refreshing...' : 'Refresh'}
                </button>
              </CardHeader>
                {generatedReports.length === 0 && !loadingReports && (
                  <ThemedText type="small" style={{ color: theme.colors.muted, padding: `${theme.spacing.sm} 0` }}>
                    No reports generated yet.
                  </ThemedText>
                )}
                {generatedReports.slice(0, 3).map(report => (
                  <div key={report.id} style={{
                    padding: `${theme.spacing.sm} 0`,
                    borderBottom: `1px solid ${theme.colors.border}`,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: theme.spacing.xs
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      <ThemedText type="small" style={{ fontWeight: 600 }}>
                        {report.pdfTemplateName || 'Report'}
                      </ThemedText>
                      <StatusBadge theme={theme} status={
                        report.status === 'Completed' ? 'Approved' :
                        report.status === 'Failed' ? 'Rejected' :
                        report.status === 'Processing' ? 'InProgress' : 'Draft'
                      }>
                        {report.status}
                      </StatusBadge>
                    </div>
                    <ThemedText type="small" style={{ color: theme.colors.muted, fontSize: '0.75rem' }}>
                      {formatDate(report.createdAt)}
                      {report.fileSizeBytes && ` - ${(report.fileSizeBytes / 1024).toFixed(0)} KB`}
                    </ThemedText>
                    <div style={{ display: 'flex', gap: theme.spacing.xs }}>
                      {report.status === 'Completed' && (
                        <>
                          <Button
                            variant="primary"
                            size="small"
                            onClick={() => handleDownloadReport(report.id)}
                            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                          >
                            Download
                          </Button>
                          <Button
                            variant="secondary"
                            size="small"
                            onClick={() => setShareReportId(report.id)}
                            style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                          >
                            Share
                          </Button>
                        </>
                      )}
                      {report.status === 'Failed' && (
                        <Button
                          variant="secondary"
                          size="small"
                          onClick={() => handleRegenerateReport(report.id)}
                          style={{ fontSize: '0.75rem', padding: '4px 8px' }}
                        >
                          Retry
                        </Button>
                      )}
                    </div>
                    {report.status === 'Failed' && report.errorMessage && (
                      <ThemedText type="small" style={{ color: theme.colors.error, fontSize: '0.7rem' }}>
                        {report.errorMessage}
                      </ThemedText>
                    )}
                  </div>
                ))}
                {generatedReports.length > 3 && (
                  <ThemedText type="small" style={{ color: theme.colors.muted, padding: `${theme.spacing.xs} 0` }}>
                    Showing 3 of {generatedReports.length} reports
                  </ThemedText>
                )}
                <Button
                  variant="secondary"
                  size="small"
                  onClick={() => navigate(`/generated-reports?submissionId=${submissionId}`)}
                  style={{ fontSize: '0.75rem', padding: '4px 8px', marginTop: theme.spacing.sm }}
                >
                  Manage Reports
                </Button>
              </Card>

            {/* Status & Info */}
            <Card theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Status & Information</ThemedText>
              </CardHeader>

              <InfoRow theme={theme}>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  Status:
                </ThemedText>
                <StatusBadge theme={theme} status={submission.status}>
                  {getSubmissionStatusLabel(submission.status)}
                </StatusBadge>
              </InfoRow>

              {/* Status change actions for admins */}
              {(isAdmin() || isSuperAdmin()) && (
                <div style={{ display: 'flex', gap: theme.spacing.sm, flexWrap: 'wrap', marginBottom: theme.spacing.sm }}>
                  {submission.status === SubmissionStatus.SUBMITTED && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleStatusChange(SubmissionStatus.UNDER_REVIEW)}
                      disabled={statusUpdating}
                    >
                      <Clock size={14} style={{ marginRight: 4 }} />
                      {statusUpdating ? 'Updating...' : 'Start Review'}
                    </Button>
                  )}
                  {submission.status === SubmissionStatus.UNDER_REVIEW && (
                    <>
                      <Button
                        size="sm"
                        onClick={() => openStatusModal('approve')}
                        disabled={statusUpdating}
                        style={{ backgroundColor: StatusPalette.green, color: '#fff', border: 'none' }}
                      >
                        <Check size={14} style={{ marginRight: 4 }} />
                        Approve
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => openStatusModal('reject')}
                        disabled={statusUpdating}
                        style={{ backgroundColor: StatusPalette.red, color: '#fff', border: 'none' }}
                      >
                        <X size={14} style={{ marginRight: 4 }} />
                        Reject
                      </Button>
                    </>
                  )}
                  {submission.status === SubmissionStatus.REJECTED && (
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={() => handleStatusChange(SubmissionStatus.UNDER_REVIEW)}
                      disabled={statusUpdating}
                    >
                      <Clock size={14} style={{ marginRight: 4 }} />
                      {statusUpdating ? 'Updating...' : 'Re-open Review'}
                    </Button>
                  )}
                </div>
              )}

              <InfoRow theme={theme}>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  Submitted:
                </ThemedText>
                {canEdit && isEditMode && allowEditSubmissionDateTime ? (
                  <input
                    type="datetime-local"
                    value={editedMeta.submittedAtLocal ?? toLocalInputValue(submission.submittedAt || submission.createdAt)}
                    onChange={(e) => setEditedMeta(prev => ({ ...prev, submittedAtLocal: e.target.value }))}
                    style={metaInputStyle}
                  />
                ) : (
                  <ThemedText type="small" style={{ fontWeight: 600 }}>
                    {formatDate(submission.submittedAt || submission.createdAt)}
                  </ThemedText>
                )}
              </InfoRow>

              <InfoRow theme={theme}>
                <ThemedText type="small" style={{ color: theme.colors.muted }}>
                  Submitter:
                </ThemedText>
                {canEdit && isEditMode && allowEditSubmissionSubmitter && isAdmin() ? (
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6, alignItems: 'flex-end' }}>
                    <input
                      type="text"
                      value={editedMeta.submitterName ?? (submission.submitterName || '')}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, submitterName: e.target.value }))}
                      placeholder="Submitter name"
                      style={metaInputStyle}
                    />
                    <input
                      type="email"
                      value={editedMeta.submitterEmail ?? (submission.submitterEmail || '')}
                      onChange={(e) => setEditedMeta(prev => ({ ...prev, submitterEmail: e.target.value }))}
                      placeholder="Submitter email"
                      style={metaInputStyle}
                    />
                  </div>
                ) : (
                  <div style={{ textAlign: 'right' }}>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      {submission.submitterName || 'Anonymous'}
                    </ThemedText>
                    {submission.submitterEmail && (
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        {submission.submitterEmail}
                      </ThemedText>
                    )}
                  </div>
                )}
              </InfoRow>

              {submission.deviceId && (
                <InfoRow theme={theme}>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Device:
                  </ThemedText>
                  <ThemedText type="small" style={{ fontWeight: 600 }}>
                    {submission.deviceId.substring(0, 20)}...
                  </ThemedText>
                </InfoRow>
              )}

            </Card>

            {/* Work Order Card */}
            <Card theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Work Order</ThemedText>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={openWorkOrderModal}
                >
                  {linkedWorkOrder ? 'Change' : 'Assign'}
                </Button>
              </CardHeader>

              {linkedWorkOrder ? (
                <>
                  <div
                    style={{
                      padding: theme.spacing.md,
                      borderRadius: theme.borderRadius.md,
                      border: `1px solid ${theme.colors.border}`,
                      backgroundColor: `${theme.colors.tint}08`,
                      cursor: 'pointer',
                    }}
                    onClick={() => navigate(`/work-orders/${linkedWorkOrder.id}`)}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: theme.spacing.xs }}>
                      <ThemedText type="small" style={{ fontWeight: 700, color: theme.colors.tint }}>
                        {linkedWorkOrder.workOrderNumber || `WO-${linkedWorkOrder.id}`}
                      </ThemedText>
                      <span style={{
                        fontSize: '11px',
                        fontWeight: 600,
                        padding: '2px 8px',
                        borderRadius: '4px',
                        backgroundColor: withOpacity(WorkOrderStatusColors[linkedWorkOrder.status] || theme.colors.muted),
                        color: WorkOrderStatusColors[linkedWorkOrder.status] || theme.colors.muted,
                      }}>
                        {linkedWorkOrder.status === 'InProgress' ? 'In Progress' :
                         linkedWorkOrder.status === 'OnHold' ? 'On Hold' :
                         linkedWorkOrder.status || 'Open'}
                      </span>
                    </div>
                    <ThemedText type="small" style={{ fontWeight: 600, marginBottom: theme.spacing.xs, display: 'block' }}>
                      {linkedWorkOrder.title}
                    </ThemedText>
                    {linkedWorkOrder.customerName && (
                      <ThemedText type="small" style={{ color: theme.colors.muted, display: 'block', fontSize: '12px' }}>
                        Customer: {linkedWorkOrder.customerName}
                      </ThemedText>
                    )}
                    {linkedWorkOrder.locationName && (
                      <ThemedText type="small" style={{ color: theme.colors.muted, display: 'block', fontSize: '12px' }}>
                        Location: {linkedWorkOrder.locationName}
                      </ThemedText>
                    )}
                    {linkedWorkOrder.serviceDate && (
                      <ThemedText type="small" style={{ color: theme.colors.muted, display: 'block', fontSize: '12px', marginTop: theme.spacing.xs }}>
                        Service: {formatDateOnly(linkedWorkOrder.serviceDate)}
                      </ThemedText>
                    )}
                  </div>
                  <div style={{ marginTop: theme.spacing.sm, textAlign: 'right' }}>
                    <Button
                      variant="secondary"
                      size="sm"
                      style={{ color: StatusPalette.red, fontSize: '12px' }}
                      onClick={() => handleAssignWorkOrder(null)}
                    >
                      Remove Link
                    </Button>
                  </div>
                </>
              ) : (
                <div style={{
                  textAlign: 'center',
                  padding: `${theme.spacing.lg} ${theme.spacing.md}`,
                  color: theme.colors.muted,
                }}>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    No work order linked
                  </ThemedText>
                  <div style={{ marginTop: theme.spacing.sm }}>
                    <Button variant="secondary" size="sm" onClick={openWorkOrderModal}>
                      Assign Work Order
                    </Button>
                  </div>
                </div>
              )}
            </Card>

            {/* Submission Timeline */}
            <Card theme={theme}>
              <CardHeader theme={theme}>
                <ThemedText type="subtitle">Submission Timeline</ThemedText>
              </CardHeader>

              <TimelineContainer theme={theme}>
                {/* Step 1: Form Started */}
                <TimelineItem theme={theme} isActive={!!submission.formStartedAt} isLast={false}>
                  <TimelineLabel theme={theme}>
                    <TimelineIcon><Pencil size={14} /></TimelineIcon>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      Form Started
                    </ThemedText>
                  </TimelineLabel>
                  {submission.formStartedAt ? (
                    <>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        {formatDate(submission.formStartedAt)}
                      </ThemedText>
                      {submission.submittedAt && (
                        <TimelineDuration theme={theme}>
                          <Clock size={12} /> Filling time: {formatDuration(submission.formStartedAt, submission.submittedAt)}
                        </TimelineDuration>
                      )}
                    </>
                  ) : (
                    <TimelineNote theme={theme}>Not recorded</TimelineNote>
                  )}
                </TimelineItem>

                {/* Step 2: Submitted */}
                <TimelineItem theme={theme} isActive={!!submission.submittedAt} isLast={false}>
                  <TimelineLabel theme={theme}>
                    <TimelineIcon><Check size={14} /></TimelineIcon>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      Submitted
                    </ThemedText>
                  </TimelineLabel>
                  {submission.submittedAt ? (
                    <>
                      <ThemedText type="small" style={{ color: theme.colors.muted }}>
                        {formatDate(submission.submittedAt)}
                      </ThemedText>
                      {submission.isOfflineSubmission && submission.createdAt && (
                        <TimelineDuration theme={theme}>
                          <Wifi size={12} /> Sync delay: {formatDuration(submission.submittedAt, submission.createdAt)}
                        </TimelineDuration>
                      )}
                    </>
                  ) : (
                    <TimelineNote theme={theme}>Not yet submitted</TimelineNote>
                  )}
                </TimelineItem>

                {/* Step 3: Synced to Server */}
                <TimelineItem theme={theme} isActive={!!submission.createdAt} isLast={true}>
                  <TimelineLabel theme={theme}>
                    <TimelineIcon><Cloud size={14} /></TimelineIcon>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      {submission.isOfflineSubmission ? 'Synced to Server' : 'Received by Server'}
                    </ThemedText>
                  </TimelineLabel>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    {formatDate(submission.createdAt)}
                  </ThemedText>
                  {submission.isOfflineSubmission && (
                    <TimelineNote theme={theme}>
                      Offline submission synced later
                    </TimelineNote>
                  )}
                </TimelineItem>
              </TimelineContainer>

              {/* Total Duration Summary */}
              {submission.formStartedAt && submission.createdAt && (
                <div style={{
                  marginTop: theme.spacing.lg,
                  paddingTop: theme.spacing.md,
                  borderTop: `1px solid ${theme.colors.border}`
                }}>
                  <InfoRow theme={theme}>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      Total Duration:
                    </ThemedText>
                    <ThemedText type="small" style={{ fontWeight: 600, color: theme.colors.tint }}>
                      {formatDuration(submission.formStartedAt, submission.createdAt)}
                    </ThemedText>
                  </InfoRow>
                </div>
              )}
            </Card>

            {/* Location */}
            {(submission.latitude || submission.longitude || editingLocation) && (
              <Card theme={theme}>
                <CardHeader theme={theme}>
                  <ThemedText type="subtitle">Submission Location</ThemedText>
                </CardHeader>

                {(effLat != null && effLng != null) && (
                  <InfoRow theme={theme}>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      Coordinates:
                    </ThemedText>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      {Number(effLat).toFixed(6)}, {Number(effLng).toFixed(6)}
                    </ThemedText>
                  </InfoRow>
                )}

                {!editingLocation && submission.locationName && (
                  <InfoRow theme={theme}>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      Address:
                    </ThemedText>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      {submission.locationName}
                    </ThemedText>
                  </InfoRow>
                )}

                <div style={{ marginTop: theme.spacing.md }}>
                  {editingLocation ? (
                    <LocationEditorMap
                      theme={theme}
                      latitude={effLat ?? null}
                      longitude={effLng ?? null}
                      onChange={(lat, lng) =>
                        setEditedMeta(prev => ({
                          ...prev,
                          latitude: lat,
                          longitude: lng,
                          locationName: `${lat.toFixed(4)}, ${lng.toFixed(4)}`,
                        }))
                      }
                    />
                  ) : (
                    <MiniMap
                      latitude={submission.latitude}
                      longitude={submission.longitude}
                      height="200px"
                      zoom={13}
                      label={submission.locationName || 'Submission Location'}
                      showButtons={true}
                    />
                  )}
                </div>
              </Card>
            )}
          </Sidebar>
        </ContentGrid>

        {/* Location Modal */}
        {(submission.latitude || submission.longitude) && (
          <Modal theme={theme} isOpen={locationModalOpen} onClick={() => setLocationModalOpen(false)}>
            <ModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
              <ModalHeader theme={theme}>
                <ThemedText type="subtitle">Submission Location</ThemedText>
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setLocationModalOpen(false)}
                >
                  <X size={14} />
                </Button>
              </ModalHeader>

              <div style={{ marginBottom: theme.spacing.md }}>
                <InfoRow theme={theme}>
                  <ThemedText type="small" style={{ color: theme.colors.muted }}>
                    Coordinates:
                  </ThemedText>
                  <ThemedText type="small" style={{ fontWeight: 600 }}>
                    {submission.latitude.toFixed(6)}, {submission.longitude.toFixed(6)}
                  </ThemedText>
                </InfoRow>
                {submission.locationName && (
                  <InfoRow theme={theme}>
                    <ThemedText type="small" style={{ color: theme.colors.muted }}>
                      Address:
                    </ThemedText>
                    <ThemedText type="small" style={{ fontWeight: 600 }}>
                      {submission.locationName}
                    </ThemedText>
                  </InfoRow>
                )}
              </div>

              <LargeMap theme={theme}>
                <MapContainer
                  center={[submission.latitude, submission.longitude]}
                  zoom={13}
                  scrollWheelZoom={true}
                  style={{ height: '100%', width: '100%' }}
                  zoomControl={true}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[submission.latitude, submission.longitude]}>
                    <Popup>
                      {submission.locationName && <><strong>{submission.locationName}</strong><br /></>}
                      {submission.latitude.toFixed(6)}, {submission.longitude.toFixed(6)}
                    </Popup>
                  </Marker>
                </MapContainer>
              </LargeMap>

              <MapButtonsRow theme={theme}>
                <MapLinksDropdown
                  latitude={submission.latitude}
                  longitude={submission.longitude}
                  label="Open in Maps"
                  openUpward={true}
                />
              </MapButtonsRow>
            </ModalContent>
          </Modal>
        )}

        {/* Image Modal */}
        {selectedImage && (
          <ImageModalOverlay
            theme={theme}
            isOpen={imageModalOpen}
            onClick={() => {
              setImageModalOpen(false);
              setSelectedImage(null);
            }}
          >
            <ImageModalContent theme={theme} onClick={(e) => e.stopPropagation()}>
              <CloseButton
                theme={theme}
                onClick={() => {
                  setImageModalOpen(false);
                  setSelectedImage(null);
                }}
              >
                <X size={24} />
              </CloseButton>
              <FullSizeImage
                theme={theme}
                src={selectedImage.fileUrl}
                alt={selectedImage.fileName}
                onError={(e) => {
                  e.target.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="400"%3E%3Crect fill="%23ddd" width="400" height="400"/%3E%3Ctext x="50%25" y="50%25" text-anchor="middle" dy=".3em" fill="%23999"%3EImage unavailable%3C/text%3E%3C/svg%3E';
                }}
              />
              <div style={{ color: 'white', marginTop: '16px', textAlign: 'center' }}>
                <ThemedText style={{ color: 'white' }}>{selectedImage.fileName}</ThemedText>
                <ThemedText style={{ color: 'rgba(255,255,255,0.7)', fontSize: '14px' }}>
                  {(selectedImage.fileSize / 1024).toFixed(2)} KB • {selectedImage.contentType}
                </ThemedText>
              </div>
            </ImageModalContent>
          </ImageModalOverlay>
        )}
        {/* Status Change Confirmation Modal */}
        <Modal theme={theme} isOpen={statusModalOpen} onClick={() => { setStatusModalOpen(false); setStatusComment(''); setStatusModalAction(null); }}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '500px' }}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">
                {statusModalAction === 'approve' ? 'Approve Submission' : 'Reject Submission'}
              </ThemedText>
              <Button variant="secondary" size="sm" onClick={() => { setStatusModalOpen(false); setStatusComment(''); setStatusModalAction(null); }}>
                <X size={14} />
              </Button>
            </ModalHeader>

            <div style={{ marginBottom: theme.spacing.md }}>
              <ThemedText type="small" style={{ color: theme.colors.muted, marginBottom: theme.spacing.sm, display: 'block' }}>
                {statusModalAction === 'reject'
                  ? 'Please provide a reason for rejecting this submission (required):'
                  : 'Add an optional comment for this approval:'}
              </ThemedText>
              <textarea
                value={statusComment}
                onChange={(e) => setStatusComment(e.target.value)}
                placeholder={statusModalAction === 'reject' ? 'Reason for rejection...' : 'Optional comment...'}
                rows={3}
                style={{
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: theme.colors.backgroundSecondary,
                  color: theme.colors.text,
                  fontSize: theme.typography.sizes.default,
                  fontFamily: 'inherit',
                  resize: 'vertical',
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{ display: 'flex', justifyContent: 'flex-end', gap: theme.spacing.sm }}>
              <Button
                variant="secondary"
                size="sm"
                onClick={() => { setStatusModalOpen(false); setStatusComment(''); setStatusModalAction(null); }}
              >
                Cancel
              </Button>
              <Button
                size="sm"
                disabled={statusUpdating || (statusModalAction === 'reject' && !statusComment.trim())}
                onClick={() => handleStatusChange(
                  statusModalAction === 'approve' ? SubmissionStatus.APPROVED : SubmissionStatus.REJECTED,
                  statusComment.trim()
                )}
                style={statusModalAction === 'approve'
                  ? { backgroundColor: StatusPalette.green, color: '#fff', border: 'none' }
                  : { backgroundColor: StatusPalette.red, color: '#fff', border: 'none' }
                }
              >
                {statusUpdating ? 'Updating...' : statusModalAction === 'approve' ? 'Confirm Approve' : 'Confirm Reject'}
              </Button>
            </div>
          </ModalContent>
        </Modal>

        {/* Work Order Assignment Modal */}
        <Modal theme={theme} isOpen={woModalOpen} onClick={() => setWoModalOpen(false)}>
          <ModalContent theme={theme} onClick={(e) => e.stopPropagation()} style={{ maxWidth: '600px' }}>
            <ModalHeader theme={theme}>
              <ThemedText type="subtitle">
                {linkedWorkOrder ? 'Change Work Order' : 'Assign Work Order'}
              </ThemedText>
              <Button variant="secondary" size="sm" onClick={() => setWoModalOpen(false)}>
                <X size={14} />
              </Button>
            </ModalHeader>

            <div style={{ marginBottom: theme.spacing.md }}>
              <input
                type="text"
                placeholder="Search work orders..."
                value={woSearchTerm}
                onChange={(e) => setWoSearchTerm(e.target.value)}
                style={{
                  width: '100%',
                  padding: `${theme.spacing.sm} ${theme.spacing.md}`,
                  borderRadius: theme.borderRadius.md,
                  border: `1px solid ${theme.colors.border}`,
                  backgroundColor: theme.colors.backgroundSecondary,
                  color: theme.colors.text,
                  fontSize: theme.typography.sizes.default,
                  outline: 'none',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {loadingWorkOrders ? (
              <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.muted }}>
                Loading work orders...
              </div>
            ) : filteredWorkOrders.length === 0 ? (
              <div style={{ textAlign: 'center', padding: theme.spacing.xl, color: theme.colors.muted }}>
                {woSearchTerm ? 'No work orders match your search' : 'No work orders available'}
              </div>
            ) : (
              <div style={{ maxHeight: '400px', overflowY: 'auto', display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                {filteredWorkOrders.map((wo) => {
                  const isCurrentlyLinked = linkedWorkOrder && linkedWorkOrder.id === wo.id;
                  return (
                    <div
                      key={wo.id}
                      onClick={() => !isCurrentlyLinked && !assigningWorkOrder && handleAssignWorkOrder(wo.id)}
                      style={{
                        padding: theme.spacing.md,
                        borderRadius: theme.borderRadius.md,
                        border: `1px solid ${isCurrentlyLinked ? theme.colors.tint : theme.colors.border}`,
                        backgroundColor: isCurrentlyLinked ? `${theme.colors.tint}10` : theme.colors.background,
                        cursor: isCurrentlyLinked ? 'default' : 'pointer',
                        opacity: assigningWorkOrder ? 0.6 : 1,
                        transition: 'all 0.15s ease',
                      }}
                      onMouseEnter={(e) => {
                        if (!isCurrentlyLinked) e.currentTarget.style.borderColor = theme.colors.tint;
                      }}
                      onMouseLeave={(e) => {
                        if (!isCurrentlyLinked) e.currentTarget.style.borderColor = theme.colors.border;
                      }}
                    >
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                        <div style={{ flex: 1 }}>
                          <div style={{ display: 'flex', alignItems: 'center', gap: theme.spacing.sm, marginBottom: '2px' }}>
                            <ThemedText type="small" style={{ fontWeight: 700, color: theme.colors.tint }}>
                              {wo.workOrderNumber || `WO-${wo.id}`}
                            </ThemedText>
                            <span style={{
                              fontSize: '10px',
                              fontWeight: 600,
                              padding: '1px 6px',
                              borderRadius: '3px',
                              backgroundColor: withOpacity(WorkOrderStatusColors[wo.status] || theme.colors.muted),
                              color: WorkOrderStatusColors[wo.status] || theme.colors.muted,
                            }}>
                              {wo.status === 'InProgress' ? 'In Progress' :
                               wo.status === 'OnHold' ? 'On Hold' :
                               wo.status || 'Open'}
                            </span>
                          </div>
                          <ThemedText type="small" style={{ fontWeight: 600 }}>
                            {wo.title}
                          </ThemedText>
                          <div style={{ display: 'flex', gap: theme.spacing.md, marginTop: '4px', flexWrap: 'wrap' }}>
                            {wo.customerName && (
                              <ThemedText type="small" style={{ color: theme.colors.muted, fontSize: '12px' }}>
                                {wo.customerName}
                              </ThemedText>
                            )}
                            {wo.locationName && (
                              <ThemedText type="small" style={{ color: theme.colors.muted, fontSize: '12px' }}>
                                {wo.locationName}
                              </ThemedText>
                            )}
                          </div>
                        </div>
                        {isCurrentlyLinked && (
                          <span style={{
                            fontSize: '11px',
                            fontWeight: 600,
                            color: theme.colors.tint,
                            whiteSpace: 'nowrap',
                          }}>
                            Current
                          </span>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ModalContent>
        </Modal>
      </PageContainer>

      <ShareReportModal
        isOpen={shareReportId !== null}
        reportId={shareReportId}
        onClose={() => setShareReportId(null)}
      />

      {linkPicker.open && linkPicker.field && (
        <LinkExistingSubmissionPicker
          isOpen={linkPicker.open}
          onClose={() => setLinkPicker({ open: false, field: null })}
          parentId={submission?.id}
          fieldId={linkPicker.field.id}
          schemaId={linkPicker.field.props?.configuration?.repeatedFormSchemaId}
          schemaName={linkPicker.field.props?.configuration?.repeatedFormDisplayName || linkPicker.field.props?.label || 'submission'}
          existingChildIds={(() => {
            const fieldValue = linkPicker.field ? submissionData[linkPicker.field.id] : null;
            const idSet = Array.isArray(fieldValue) ? new Set(fieldValue.map(Number)) : null;
            const targetSchemaId = linkPicker.field?.props?.configuration?.repeatedFormSchemaId;
            return (submission?.childSubmissions || [])
              .filter(c => {
                if (idSet && idSet.size > 0) return idSet.has(Number(c.id));
                return targetSchemaId && Number(c.formSchemaId) === Number(targetSchemaId);
              })
              .map(c => c.id);
          })()}
          onLinked={async (count) => {
            toast.success(`Linked ${count} submission${count === 1 ? '' : 's'}`);
            // Re-fetch parent so the new linked children appear.
            try {
              const refreshed = await formSubmissionService.getSubmissionById(submission.id, { includeChildren: true });
              setSubmission(refreshed);
            } catch {
              // Re-fetch failure is non-fatal; the user can refresh manually.
            }
          }}
        />
      )}
    </Layout>
  );
};

export default SubmissionDetailPage;
